import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SendIcon from '@mui/icons-material/Send';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FieldMultipleAnswers from '../../../components/general/surveysField/fieldMultipleAnswers';
import FieldUniqueAnswer from '../../../components/general/surveysField/fieldUniqueAnswer';
import FieldLinealAnswer from '../../../components/general/surveysField/fieldLinealAnswer';
import FieldLinealLabelAnswer from '../../../components/general/surveysField/fieldLinealLabelAnswer';
import FieldTextAnswer from '../../../components/general/surveysField/fieldTextAnswer';
import FieldListAnswer from '../../../components/general/surveysField/fieldUniqueAnswerList';
import FieldOpenTextAnswer from '../../../components/general/surveysField/fieldOpenTextAnswer';
import FieldTextValidateAnswer from '../../../components/general/surveysField/fieldTextValidateAnswer';

import {
    asyncGetSurvey,
    asyncPostSurveyResponse,
} from '../../../../services/survey.service';
import { useDispatch } from 'react-redux';
import { typesRedux } from '../../../../shared/types/typesRedux';
import {
    allowedUrlParamToQuestionDescription,
    isRequiredAnswerComplete,
} from '../../../../shared/utils/survey';
import { LabelAnswer } from '../../../components/general/surveysField/labelAnswer';

interface Props {
    onSave: () => void;
    onChangeSurvey: (survey: any) => void;
    dominantColor?: string;
    fontFamily?: string;
}

export const FormRender = ({
    onSave,
    onChangeSurvey,
    dominantColor,
    fontFamily,
}: Props) => {
    const AUTO_NAME_QUESTION_ID = '__auto_nombre__';
    const AUTO_EMAIL_QUESTION_ID = '__auto_email__';
    const dispatch: any = useDispatch();
    const { idSurvey } = useParams();
    const [survey, setSurvey] = useState<any>(undefined);
    const [questions, setQuestions] = useState<any[]>([]);
    const [answers, setAnswers] = useState({});
    const [autoFillAnchorQuestionId, setAutoFillAnchorQuestionId] = useState<
        string | null
    >(null);
    const [loading, setLoading] = useState<boolean>(false);
    const location = useLocation();
    const [surveyDisable, setSurveyDisable] = useState<string>('');

    const queryParams = new URLSearchParams(location.search);

    useEffect(() => {
        if (!questions?.length) return;

        const paramUrlAnswers: Record<string, any> = {};
        let hasNewAnswer = false;

        const safeDecode = (input: string) => {
            try {
                return decodeURIComponent(input);
            } catch (e) {
                return '';
            }
        };

        const sanitize = (input: string) => {
            return safeDecode(input)
                .replace(/[<>]/g, '')
                .trim()
                .substring(0, 100);
        };

        const isValidAnswerForQuestion = (type: string, value: any) => {
            switch (type) {
                case 'only':
                case 'list-only':
                case 'validation-reference':
                case 'text':
                case 'open-text':
                    return typeof value === 'string' && value.length < 500;
                case 'multiple':
                    return (
                        Array.isArray(value) &&
                        value.every((v) => typeof v === 'string')
                    );
                case 'linear':
                    return !isNaN(parseInt(value));
                default:
                    return typeof value === 'string';
            }
        };

        queryParams.forEach((value, key) => {
            const expectedDescription =
                allowedUrlParamToQuestionDescription?.[key];
            if (!expectedDescription) return;

            const matchedQuestion = questions.find(
                (item) =>
                    item?.description?.trim().toLowerCase() ===
                    expectedDescription.trim().toLowerCase()
            );

            if (matchedQuestion && !(answers as any)[matchedQuestion.id]) {
                const cleanedValue = sanitize(value);

                if (
                    !isValidAnswerForQuestion(
                        matchedQuestion.type,
                        cleanedValue
                    )
                ) {
                    return;
                }

                paramUrlAnswers[matchedQuestion.id] = {
                    idQuestion: matchedQuestion.id,
                    answer: cleanedValue,
                    type: matchedQuestion.type,
                };

                hasNewAnswer = true;
            }
        });

        if (hasNewAnswer) {
            setAnswers((prev) => ({
                ...prev,
                ...paramUrlAnswers,
            }));
        }
    }, [questions]);

    useEffect(() => {
        setLoading(true);
        asyncGetSurvey(idSurvey, true)
            .then((body: any) => {
                if (!body?.items || body.items.status !== 'active') {
                    setSurvey(null);
                    setSurveyDisable('La encuesta no está disponible.');
                    onChangeSurvey(null);
                } else {
                    setSurvey(body.items);
                    onChangeSurvey(body.items);
                    setSurveyDisable('');
                }
            })
            .catch(() => {
                setSurvey(null);
                setSurveyDisable('La encuesta no está disponible.');
                onChangeSurvey(null);
            })
            .finally(() => setLoading(false));
    }, [idSurvey]);

    const handleChangeQuestion = (data: any) => {
        setAnswers((prev: any) => ({ ...prev, [data.idQuestion]: data }));
    };

    const fillTextQuestionsFromDataset = useCallback(
        (
            record: { nombre: string; email: string },
            validationQuestionId: string
        ) => {
            const normalizedName = String(
                (record as any)?.nombre ?? (record as any)?.name ?? ''
            ).trim();
            const normalizedEmail = String(
                (record as any)?.email ??
                    (record as any)?.correo ??
                    (record as any)?.mail ??
                    ''
            ).trim();

            setAutoFillAnchorQuestionId(validationQuestionId);
            setAnswers((prev: any) => {
                return {
                    ...prev,
                    [AUTO_NAME_QUESTION_ID]: {
                        idQuestion: AUTO_NAME_QUESTION_ID,
                        answer: normalizedName,
                        type: 'text',
                        why: '',
                        error: !normalizedName,
                    },
                    [AUTO_EMAIL_QUESTION_ID]: {
                        idQuestion: AUTO_EMAIL_QUESTION_ID,
                        answer: normalizedEmail,
                        type: 'text',
                        why: '',
                        error: !normalizedEmail,
                    },
                };
            });
        },
        [AUTO_EMAIL_QUESTION_ID, AUTO_NAME_QUESTION_ID]
    );

    const getResAnswers = () => {
        const resAnswers: any = {};
        questions.forEach((q: any) => {
            if (
                q.id === AUTO_NAME_QUESTION_ID ||
                q.id === AUTO_EMAIL_QUESTION_ID
            )
                return;
            if ((answers as any)[q.id]) {
                resAnswers[q.id] = (answers as any)[q.id];
            }
        });
        return resAnswers;
    };

    const isInvalid = () =>
        questions
            .filter((q) => q.required)
            .some(
                (q) =>
                    !isRequiredAnswerComplete(
                        q.type,
                        (answers as any)[q.id]
                    )
            );

    const onClickSave = () => {
        if (isInvalid()) return;

        const resAnswers = getResAnswers();
        const data: any = {
            survey: idSurvey,
            response: resAnswers,
        };

        setLoading(true);
        asyncPostSurveyResponse(data)
            .then((body: any) => {
                if (body.items.ok) {
                    onSave();
                    setAnswers({});
                    window.scrollTo(0, 0);
                }
            })
            .finally(() => setLoading(false));
    };

    const validateDependency = ({ dependency, position }: any) => {
        const dep = dependency?.questionDependency;
        const depAnswer = dependency?.answerDependency;
        const depAnswerText = dependency?.answerTextDependency;

        if (position > dep?.position) {
            const currentAnswer = (answers as any)[dep?.id];

            if (
                currentAnswer?.answer &&
                currentAnswer?.answer.length > 0 &&
                (!depAnswer || !depAnswer?.id) &&
                (!Boolean(depAnswerText?.trim()) || depAnswerText === '-')
            )
                return true;

            switch (dep?.type) {
                case 'linear':
                    const range = depAnswerText.split('-');
                    try {
                        return (
                            parseInt(range[0]) <=
                                parseInt(currentAnswer?.answer) &&
                            parseInt(range[1]) >=
                                parseInt(currentAnswer?.answer)
                        );
                    } catch (error) {
                        return false;
                    }
                case 'multiple':
                    const isSameAnswer = Boolean(
                        currentAnswer?.answer?.find(
                            (a: string) => a === depAnswer?.id
                        )
                    );
                    return (
                        currentAnswer?.answer &&
                        depAnswer?.id &&
                        ((!dependency?.isDiferentOf && isSameAnswer) ||
                            (dependency?.isDiferentOf && !isSameAnswer))
                    );
                default:
                    return (
                        currentAnswer?.answer &&
                        depAnswer?.id &&
                        ((!dependency?.isDiferentOf &&
                            currentAnswer?.answer === depAnswer?.id) ||
                            (dependency?.isDiferentOf &&
                                currentAnswer?.answer !== depAnswer?.id))
                    );
            }
        }

        return true;
    };

    useEffect(() => {
        const baseQuestions =
            survey?.questions?.filter(
                ({ questionDependency, position, isOrDependencies }: any) => {
                    if (!questionDependency || questionDependency.length === 0)
                        return true;

                    for (const dependency of questionDependency || []) {
                        const isValid = !!validateDependency({
                            dependency,
                            position,
                        });

                        if (isOrDependencies && isValid) return true;
                        if (!isOrDependencies && !isValid) return false;
                    }

                    if (isOrDependencies) return false;
                    else return true;
                }
            ) || [];

        const autoQuestions = [
            {
                id: AUTO_NAME_QUESTION_ID,
                description: 'Nombre',
                type: 'text',
                required: false,
                answers: [],
                position: Number.MAX_SAFE_INTEGER - 1,
            },
            {
                id: AUTO_EMAIL_QUESTION_ID,
                description: 'Email',
                type: 'text',
                required: false,
                answers: [],
                position: Number.MAX_SAFE_INTEGER,
            },
        ].filter((q) => Boolean((answers as any)[q.id]));

        if (!autoQuestions.length) {
            setQuestions(baseQuestions);
            return;
        }

        const anchorIndex = baseQuestions.findIndex(
            (q: any) => q.id === autoFillAnchorQuestionId
        );

        if (anchorIndex < 0) {
            setQuestions([...baseQuestions, ...autoQuestions]);
            return;
        }

        const nextQuestions = [...baseQuestions];
        nextQuestions.splice(anchorIndex + 1, 0, ...autoQuestions);
        setQuestions(nextQuestions);
    }, [survey, answers, autoFillAnchorQuestionId]);

    const gateQuestion = useMemo(
        () =>
            questions.find(
                (q: any) =>
                    q.type === 'validation-reference' &&
                    Boolean(q.gateSurveyUntilValidated)
            ),
        [questions]
    );

    const [gateUnlocked, setGateUnlocked] = useState(false);

    useEffect(() => {
        setGateUnlocked(false);
    }, [idSurvey, survey?.id, gateQuestion?.id]);

    const visibleQuestions = useMemo(() => {
        if (!gateQuestion || gateUnlocked) {
            return questions;
        }
        return questions.filter((q: any) => q.id === gateQuestion.id);
    }, [questions, gateQuestion, gateUnlocked]);

    useEffect(() => {
        dispatch({
            type: typesRedux.SHOW_NAVBAR,
            payload: false,
        });
    }, []);

    return (
        <Grid container sx={{ textAlign: 'center', mt: 3, fontFamily: fontFamily || 'inherit', px: { xs: 2, sm: 3, md: 4 } }}>
            {!loading && surveyDisable && (
                <Grid item xs={12}>
                    <Alert
                        severity="info"
                        iconMapping={{
                            info: (
                                <InfoOutlinedIcon
                                    sx={{
                                        fontSize: '4rem',
                                        marginBottom: 2,
                                    }}
                                />
                            ),
                        }}
                        sx={{
                            backgroundColor: 'transparent',
                            color: dominantColor || '#1976d2',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '& .MuiAlert-icon': {
                                margin: 0,
                                padding: 0,
                            },
                        }}
                    >
                        <AlertTitle
                            sx={{
                                fontSize: '2.5rem',
                                fontWeight: 700,
                                marginBottom: 2,
                            }}
                        >
                            ¡Ups! Lo siento
                        </AlertTitle>

                        <Typography
                            sx={{
                                fontSize: '1.8rem',
                                fontWeight: 500,
                                maxWidth: '800px',
                            }}
                        >
                            {surveyDisable}
                        </Typography>
                    </Alert>
                </Grid>
            )}
            {survey &&
                visibleQuestions.map((q: any, index: number) => {
                    const displayIndex =
                        questions.findIndex((x: any) => x.id === q.id) + 1 ||
                        index + 1;
                    return (
                        <Grid item xs={12} mt={2} key={q.id}>
                            {q.type === 'multiple' ? (
                                <FieldMultipleAnswers
                                    key={q.id}
                                    title={`${displayIndex}. ${q?.description}`}
                                    answerQuestion={q?.answers || []}
                                    idQuestion={q.id}
                                    required={q.required}
                                    questionType={q.type}
                                    whyQuestion={q?.why}
                                    name="answer"
                                    onChange={handleChangeQuestion}
                                    dominantColor={dominantColor}
                                />
                            ) : q.type === 'only' ? (
                                <FieldUniqueAnswer
                                    key={q.id}
                                    title={`${displayIndex}. ${q?.description}`}
                                    required={q.required}
                                    answerQuestion={q?.answers || []}
                                    idQuestion={q.id}
                                    questionType={q.type}
                                    whyQuestion={q?.why}
                                    name="answer"
                                    onChange={handleChangeQuestion}
                                    dominantColor={dominantColor}
                                />
                            ) : q.type === 'linear' ? (
                                <FieldLinealAnswer
                                    key={q.id}
                                    title={`${displayIndex}. ${q?.description}`}
                                    answerQuestion={q?.answers[0] || []}
                                    idQuestion={q.id}
                                    required={q.required}
                                    questionType={q.type}
                                    questionIcon={q.icon}
                                    whyQuestion={q?.why}
                                    name="answer"
                                    onChange={handleChangeQuestion}
                                    dominantColor={dominantColor}
                                />
                            ) : q.type === 'linear-label' ? (
                                <FieldLinealLabelAnswer
                                    key={q.id}
                                    title={`${displayIndex}. ${q?.description}`}
                                    answerQuestion={q?.answers || []}
                                    idQuestion={q.id}
                                    required={q.required}
                                    questionType={q.type}
                                    questionIcon={q.icon}
                                    whyQuestion={q?.why}
                                    name="answer"
                                    onChange={handleChangeQuestion}
                                    dominantColor={dominantColor}
                                />
                            ) : q.type === 'list-only' ? (
                                <FieldListAnswer
                                    key={q.id}
                                    title={`${displayIndex}. ${q?.description}`}
                                    required={q.required}
                                    answerQuestion={q?.answers || []}
                                    idQuestion={q.id}
                                    questionType={q.type}
                                    whyQuestion={q?.why}
                                    name="answer"
                                    onChange={handleChangeQuestion}
                                    dominantColor={dominantColor}
                                />
                            ) : q.type === 'label' ? (
                                <LabelAnswer
                                    key={q.id}
                                    text={`${displayIndex}. ${q?.description}`}
                                    isRequired={false}
                                    dominantColor={dominantColor}
                                />
                            ) : q.type === 'open-text' ? (
                                <FieldOpenTextAnswer
                                    key={q.id}
                                    title={`${displayIndex}. ${q?.description}`}
                                    idQuestion={q.id}
                                    questionType={q.type}
                                    required={q.required}
                                    onChange={handleChangeQuestion}
                                    value={(answers as any)?.[q.id]?.answer}
                                />
                            ) : q.type === 'validation-reference' ? (
                                <FieldTextValidateAnswer
                                    key={q.id}
                                    title={`${displayIndex}. ${q?.description}`}
                                    idQuestion={q.id}
                                    questionType={q.type}
                                    required={q.required}
                                    placeholder="Escribe el valor a validar"
                                    onChange={handleChangeQuestion}
                                    value={(answers as any)?.[q.id]?.answer}
                                    dominantColor={dominantColor}
                                    surveyId={idSurvey}
                                    onVerifiedChange={
                                        q.gateSurveyUntilValidated
                                            ? setGateUnlocked
                                            : undefined
                                    }
                                    onValidationDataset={(record) =>
                                        fillTextQuestionsFromDataset(
                                            record,
                                            q.id
                                        )
                                    }
                                />
                            ) : (
                                <FieldTextAnswer
                                    key={q.id}
                                    title={`${displayIndex}. ${q?.description}`}
                                    idQuestion={q.id}
                                    questionType={q.type}
                                    required={q.required}
                                    placeholder="Dejanos aquí tus comentarios"
                                    onChange={handleChangeQuestion}
                                    type={q.type}
                                    subtype={q.subtype}
                                    value={(answers as any)?.[q.id]?.answer}
                                    dominantColor={dominantColor}
                                    disabled={q.id === AUTO_NAME_QUESTION_ID || q.id === AUTO_EMAIL_QUESTION_ID}
                                />
                            )}
                        </Grid>
                    );
                })}
            {survey &&
                questions &&
                questions.length > 0 &&
                (!gateQuestion || gateUnlocked) && (
                <Grid item xs={12} mt={3}>
                    <LoadingButton
                        loading={loading}
                        disabled={isInvalid()}
                        type="submit"
                        onClick={onClickSave}
                        variant="contained"
                        endIcon={<SendIcon />}
                        sx={{
                            height: '35px',
                            borderRadius: '30px',
                            backgroundColor: dominantColor || '#3C92FF',
                            ':hover': {
                                backgroundColor: dominantColor ? `${dominantColor}CC` : '#0C53EC',
                            },
                        }}
                    >
                        <Typography
                            fontFamily={fontFamily || 'Lucida Sans'}
                            fontSize="14px"
                            textTransform="capitalize"
                        >
                                Enviar Respuestas
                        </Typography>
                    </LoadingButton>
                </Grid>
            )}
        </Grid>
    );
};
